import { useState, useEffect, useRef, useCallback } from 'react'
import { motion } from 'framer-motion'
import {
  FileText,
  Upload,
  Download,
  Search,
  Filter,
  File,
  Image,
  FileSpreadsheet,
  AlertCircle,
  Check,
  X
} from 'lucide-react'
import api from '../config/api'
import { formatDate } from '../utils/formatDate'
import '../styles/MyDocuments.css'

const ALLOWED_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'image/jpeg',
  'image/png',
  'image/gif',
  'text/plain'
]

const ALLOWED_EXTENSIONS = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.jpg', '.jpeg', '.png', '.gif', '.txt']

const CATEGORIES = [
  { value: 'identification', label: 'Identification' },
  { value: 'financial', label: 'Financial' },
  { value: 'legal', label: 'Legal' },
  { value: 'correspondence', label: 'Correspondence' },
  { value: 'evidence', label: 'Evidence' },
  { value: 'contract', label: 'Contract' },
  { value: 'other', label: 'Other' }
]

function MyDocuments() {
  const [documents, setDocuments] = useState([])
  const [uploads, setUploads] = useState([])
  const [pendingSignatures, setPendingSignatures] = useState([])
  const [matters, setMatters] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [activeTab, setActiveTab] = useState('documents')
  const [searchTerm, setSearchTerm] = useState('')
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [dragActive, setDragActive] = useState(false)
  const [selectedFile, setSelectedFile] = useState(null)
  const [uploadCategory, setUploadCategory] = useState('other')
  const [uploadDescription, setUploadDescription] = useState('')
  const [uploadMatterId, setUploadMatterId] = useState('')
  const fileInputRef = useRef(null)
  const dropZoneRef = useRef(null)

  useEffect(() => {
    fetchData()
  }, [])

  // Clear success message after 4 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 4000)
      return () => clearTimeout(timer)
    }
  }, [success])

  const fetchData = async () => {
    try {
      setLoading(true)
      const [docsRes, uploadsRes, sigsRes, mattersRes] = await Promise.all([
        api.get('/documents'),
        api.get('/uploads'),
        api.get('/signatures/pending'),
        api.get('/matters').catch(() => ({ data: { data: [] } }))
      ])
      setDocuments(docsRes.data.data)
      setUploads(uploadsRes.data.data)
      setPendingSignatures(sigsRes.data.data)
      setMatters(mattersRes.data.data || [])
    } catch (err) {
      setError('Failed to load documents')
    } finally {
      setLoading(false)
    }
  }

  const validateFile = (file) => {
    if (!file) return 'No file selected'

    const extension = '.' + file.name.split('.').pop().toLowerCase()
    const isValidType = ALLOWED_TYPES.includes(file.type) || ALLOWED_EXTENSIONS.includes(extension)
    if (!isValidType) {
      return 'Invalid file type. Allowed: PDF, Word, Excel, images, and text files.'
    }

    const maxSize = 25 * 1024 * 1024 // 25MB
    if (file.size > maxSize) {
      return 'File is too large. Maximum size is 25MB.'
    }

    return null
  }

  const handleFileSelect = (file) => {
    const validationError = validateFile(file)
    if (validationError) {
      setError(validationError)
      return
    }
    setError('')
    setSelectedFile(file)
    setShowUploadModal(true)
  }

  const handleFileInputChange = (e) => {
    const file = e.target.files[0]
    if (file) handleFileSelect(file)
    // Reset input so same file can be selected again
    e.target.value = ''
  }

  const handleDrag = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }, [])

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    const file = e.dataTransfer.files?.[0]
    if (file) handleFileSelect(file)
  }, [])

  const handleUploadSubmit = async () => {
    if (!selectedFile) return

    const formData = new FormData()
    formData.append('file', selectedFile)
    formData.append('category', uploadCategory)
    formData.append('description', uploadDescription)
    if (uploadMatterId) formData.append('matterId', uploadMatterId)

    try {
      setUploading(true)
      setUploadProgress(0)
      await api.post('/documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          setUploadProgress(percent)
        }
      })
      setSuccess('Document uploaded successfully')
      resetUploadForm()
      fetchData()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload file')
    } finally {
      setUploading(false)
      setUploadProgress(0)
    }
  }

  const resetUploadForm = () => {
    setSelectedFile(null)
    setUploadCategory('other')
    setUploadDescription('')
    setUploadMatterId('')
    setShowUploadModal(false)
  }

  const handleDownload = async (documentId, filename) => {
    try {
      const response = await api.get(`/documents/${documentId}/download`)
      const { downloadUrl } = response.data.data
      window.open(downloadUrl, '_blank')
    } catch (err) {
      setError('Failed to download document')
    }
  }

  const getFileIcon = (mimeType) => {
    if (mimeType?.includes('image')) return Image
    if (mimeType?.includes('spreadsheet') || mimeType?.includes('excel')) return FileSpreadsheet
    return File
  }

  const filteredItems = (activeTab === 'documents' ? documents : uploads).filter(item =>
    item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.filename?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <div className="my-documents-page">
      <header className="page-header">
        <div className="header-content">
          <FileText size={28} />
          <div>
            <h1>Documents</h1>
            <p>View and manage your documents</p>
          </div>
        </div>
        <button
          className="btn btn-primary upload-btn"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          <Upload size={18} />
          {uploading ? 'Uploading...' : 'Upload Document'}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          hidden
          onChange={handleFileInputChange}
          accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt"
        />
      </header>

      {/* Drag and Drop Zone */}
      <div
        ref={dropZoneRef}
        className={`drop-zone ${dragActive ? 'drop-zone-active' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <Upload size={32} />
        <p className="drop-zone-text">
          {dragActive
            ? 'Drop your file here'
            : 'Drag and drop a file here, or click to browse'
          }
        </p>
        <span className="drop-zone-hint">
          PDF, Word, Excel, Images, Text &mdash; Max 25MB
        </span>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="upload-modal-overlay" onClick={() => !uploading && resetUploadForm()}>
          <motion.div
            className="upload-modal"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="upload-modal-header">
              <h3>Upload Document</h3>
              <button
                className="btn btn-icon"
                onClick={resetUploadForm}
                disabled={uploading}
              >
                <X size={18} />
              </button>
            </div>

            <div className="upload-modal-body">
              {/* Selected File Info */}
              <div className="selected-file-info">
                <File size={20} />
                <div>
                  <span className="selected-file-name">{selectedFile?.name}</span>
                  <span className="selected-file-size">{formatFileSize(selectedFile?.size)}</span>
                </div>
                {!uploading && (
                  <button
                    className="btn btn-text"
                    onClick={() => {
                      setSelectedFile(null)
                      fileInputRef.current?.click()
                    }}
                  >
                    Change
                  </button>
                )}
              </div>

              {/* Category */}
              <div className="form-group">
                <label>Category</label>
                <select
                  value={uploadCategory}
                  onChange={(e) => setUploadCategory(e.target.value)}
                  disabled={uploading}
                >
                  {CATEGORIES.map((cat) => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>

              {/* Matter Assignment */}
              {matters.length > 0 && (
                <div className="form-group">
                  <label>Assign to Matter (optional)</label>
                  <select
                    value={uploadMatterId}
                    onChange={(e) => setUploadMatterId(e.target.value)}
                    disabled={uploading}
                  >
                    <option value="">No matter selected</option>
                    {matters.map((matter) => (
                      <option key={matter.id} value={matter.id}>
                        {matter.title || matter.name || matter.caseNumber || matter.id}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Description */}
              <div className="form-group">
                <label>Description (optional)</label>
                <textarea
                  value={uploadDescription}
                  onChange={(e) => setUploadDescription(e.target.value)}
                  placeholder="Add a description for this document..."
                  rows={3}
                  disabled={uploading}
                />
              </div>

              {/* Progress Bar */}
              {uploading && (
                <div className="upload-progress">
                  <div className="upload-progress-bar">
                    <div
                      className="upload-progress-fill"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <span className="upload-progress-text">{uploadProgress}%</span>
                </div>
              )}
            </div>

            <div className="upload-modal-footer">
              <button
                className="btn btn-secondary"
                onClick={resetUploadForm}
                disabled={uploading}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleUploadSubmit}
                disabled={uploading}
              >
                <Upload size={18} />
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Pending Signatures Alert */}
      {pendingSignatures.length > 0 && (
        <motion.div
          className="signatures-alert"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="alert-content">
            <span className="alert-badge">{pendingSignatures.length}</span>
            <span>Document(s) awaiting your signature</span>
          </div>
          <div className="signature-list">
            {pendingSignatures.map((sig) => (
              <a
                key={sig.id}
                href={`/sign/${sig.id}`}
                className="signature-item"
              >
                <FileText size={16} />
                <span>{sig.documentName}</span>
              </a>
            ))}
          </div>
        </motion.div>
      )}

      {/* Tabs and Search */}
      <div className="documents-controls">
        <div className="tabs">
          <button
            className={`tab ${activeTab === 'documents' ? 'active' : ''}`}
            onClick={() => setActiveTab('documents')}
          >
            From Firm ({documents.length})
          </button>
          <button
            className={`tab ${activeTab === 'uploads' ? 'active' : ''}`}
            onClick={() => setActiveTab('uploads')}
          >
            My Uploads ({uploads.length})
          </button>
        </div>
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search documents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          <AlertCircle size={18} />
          <span>{error}</span>
          <button onClick={() => setError('')}><X size={16} /></button>
        </div>
      )}

      {success && (
        <motion.div
          className="alert alert-success"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Check size={18} />
          <span>{success}</span>
          <button onClick={() => setSuccess('')}><X size={16} /></button>
        </motion.div>
      )}

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner" />
          <p>Loading documents...</p>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="empty-state">
          <FileText size={48} />
          <h3>No documents found</h3>
          <p>
            {searchTerm
              ? 'Try adjusting your search'
              : activeTab === 'documents'
                ? 'No documents have been shared with you yet'
                : 'You haven\'t uploaded any documents yet'
            }
          </p>
          {activeTab === 'uploads' && (
            <button
              className="btn btn-primary"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload size={18} />
              Upload Your First Document
            </button>
          )}
        </div>
      ) : (
        <div className="documents-grid">
          {filteredItems.map((item, index) => {
            const FileIcon = getFileIcon(item.mimeType)
            return (
              <motion.div
                key={item.id}
                className="document-card"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.03 }}
              >
                <div className="document-icon">
                  <FileIcon size={24} />
                </div>
                <div className="document-info">
                  <h4>{item.name || item.originalFilename || item.filename}</h4>
                  <div className="document-meta">
                    <span>{item.type || item.category}</span>
                    <span>{formatFileSize(item.size)}</span>
                  </div>
                  <span className="document-date">
                    {formatDate(item.createdAt)}
                  </span>
                </div>
                {activeTab === 'documents' && (
                  <button
                    className="btn btn-icon"
                    onClick={() => handleDownload(item.id, item.name)}
                    title="Download"
                  >
                    <Download size={18} />
                  </button>
                )}
                {activeTab === 'uploads' && (
                  <span className={`upload-status status-${item.status}`}>
                    {item.status === 'approved' && <Check size={14} />}
                    {item.status}
                  </span>
                )}
              </motion.div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function formatFileSize(bytes) {
  if (!bytes) return ''
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

export default MyDocuments
